class ValidationService {
  /**
   * Validate all files
   * @param {Array} files - Array of file items
   * @param {Function} progressCallback - Progress callback function
   * @param {Function} readFileCallback - Optional callback to lazily read file content
   * @returns {Promise<Array>} - Array of validation results
   */
  static async validateFiles(files, progressCallback, readFileCallback) {
    const results = [];
    const totalFiles = files.length;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const progress = (i / totalFiles) * 100;
      
      if (progressCallback) {
        progressCallback(Math.round(progress));
      }
      
      try {
        const result = await this.validateFile(file, readFileCallback);
        results.push(result);
      } catch (error) {
        results.push({
          fileName: file.name,
          status: 'invalid',
          errors: [{
            message: `Validation failed: ${error.message}`,
            severity: 'error'
          }],
          warnings: []
        });
      }
    }
    
    if (progressCallback) {
      progressCallback(100);
    }
    
    return results;
  }

  /**
   * Validate a single file
   * @param {Object} file - File item
   * @param {Function} readFileCallback - Optional callback to lazily read file content
   * @returns {Promise<Object>} - Validation result
   */
  static async validateFile(file, readFileCallback) {
    const errors = [];
    const warnings = [];
    
    try {
      const extension = (file.extension || '').toLowerCase();
      const fileName = (file.name || '').toLowerCase();

      const requiresContent =
        extension === '.json' ||
        fileName.includes('events') ||
        fileName.includes('clusters') ||
        fileName.includes('referencepoints') ||
        (extension === '.txt' && fileName === 'data_version.txt');

      let content = file.content;

      // Lazily read content only for files that require content validation
      if ((content === undefined || content === null) && requiresContent && typeof readFileCallback === 'function') {
        try {
          content = await readFileCallback(file);
        } catch (readError) {
          errors.push({
            message: `Unable to read file content: ${readError.message || readError}`,
            severity: 'error'
          });
        }
      }

      if (extension === '.png' || extension === '.jpg' || extension === '.jpeg') {
        this.validateImageFile(file, errors, warnings);
      } else if (extension === '.glb') {
        this.validateModelFile(file, errors, warnings);
      } else if (requiresContent) {
        if (content === undefined || content === null) {
          errors.push({
            message: 'File content not available for validation',
            severity: 'error'
          });
        } else if (extension === '.json') {
          this.validateJsonFile(file, content, errors, warnings);
        } else if (fileName.includes('events')) {
          this.validateEventsFile(file, content, errors, warnings);
        } else if (fileName.includes('clusters')) {
          this.validateClustersFile(file, content, errors, warnings);
        } else if (fileName.includes('referencepoints')) {
          this.validateReferencePointsFile(file, content, errors, warnings);
        } else if (extension === '.txt' && fileName === 'data_version.txt') {
          this.validateVersionFile(file, content, errors, warnings);
        }
      }
    } catch (error) {
      errors.push({
        message: `Failed to validate file: ${error.message}`,
        severity: 'error'
      });
    }
    
    const status = errors.length > 0 ? 'invalid' : 
                   warnings.length > 0 ? 'warning' : 'valid';
    
    return {
      fileName: file.name,
      status,
      errors,
      warnings
    };
  }

  /**
   * Validate JSON file
   * @param {Object} file - File item
   * @param {Object} content - File content
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateJsonFile(file, content, errors, warnings) {
    if (!content || typeof content !== 'object') {
      errors.push({
        message: 'Invalid JSON format',
        severity: 'error'
      });
      return;
    }
    
    if (file.name === 'inspection_config.json') {
      this.validateInspectionConfig(content, errors, warnings);
    } else if (file.name === 'history_metadata.json') {
      this.validateHistoryMetadata(content, errors, warnings);
    }
  }

  /**
   * Validate inspection config
   * @param {Object} config - Configuration object
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateInspectionConfig(config, errors, warnings) {
    const requiredFields = [
      'tank_id', 'subtitle', 'standard', 'standard_description',
      'inspection_date', 'location', 'diameter_mm', 'height_mm',
      'circumference_mm', 'potential_leak'
    ];
    
    requiredFields.forEach(field => {
      if (!config[field]) {
        errors.push({
          field,
          message: `Missing required field: ${field}`,
          severity: 'error'
        });
      }
    });
    
    // Validate numeric fields
    const numericFields = ['diameter_mm', 'height_mm', 'circumference_mm'];
    numericFields.forEach(field => {
      if (config[field] && (isNaN(config[field]) || config[field] <= 0)) {
        errors.push({
          field,
          message: `${field} must be a positive number`,
          severity: 'error'
        });
      }
    });
    
    // Validate potential_leak values
    if (config.potential_leak && !['HIGH', 'MEDIUM', 'LOW'].includes(config.potential_leak)) {
      warnings.push({
        field: 'potential_leak',
        message: 'potential_leak should be one of: HIGH, MEDIUM, LOW',
        severity: 'warning'
      });
    }
  }

  /**
   * Validate history metadata
   * @param {Array} metadata - History metadata array
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateHistoryMetadata(metadata, errors, warnings) {
    if (!Array.isArray(metadata)) {
      errors.push({
        message: 'history_metadata.json should contain an array',
        severity: 'error'
      });
      return;
    }
    
    metadata.forEach((item, index) => {
      const requiredFields = ['history_group_id', 'history_id', 'title', 'subtitle'];
      
      requiredFields.forEach(field => {
        if (item[field] === undefined || item[field] === null) {
          errors.push({
            line: index + 1,
            field,
            message: `Missing required field: ${field}`,
            severity: 'error'
          });
        }
      });
      
      // Validate history_id is unique
      const duplicateIds = metadata.filter(m => m.history_id === item.history_id);
      if (duplicateIds.length > 1) {
        errors.push({
          line: index + 1,
          field: 'history_id',
          message: `Duplicate history_id: ${item.history_id}`,
          severity: 'error'
        });
      }
    });
  }

  /**
   * Validate events file
   * @param {Object} file - File item
   * @param {Array} content - File content
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateEventsFile(file, content, errors, warnings) {
    if (!Array.isArray(content) || content.length === 0) {
      errors.push({
        message: 'Events file must contain at least one event',
        severity: 'error'
      });
      return;
    }
    
    const requiredFields = ['Id', 'HHMMSS', 'MSEC', 'X', 'Y', 'A', 'E', 'CNTS'];
    
    content.forEach((event, index) => {
      requiredFields.forEach(field => {
        if (event[field] === undefined || event[field] === null) {
          errors.push({
            line: index + 2, // +2 because of header and 0-based index
            field,
            message: `Missing required field: ${field}`,
            severity: 'error'
          });
        }
      });
      
      // Validate numeric fields
      const numericFields = ['Id', 'MSEC', 'X', 'Y', 'A', 'CNTS'];
      numericFields.forEach(field => {
        if (event[field] !== undefined && isNaN(event[field])) {
          errors.push({
            line: index + 2,
            field,
            message: `${field} must be a number`,
            severity: 'error'
          });
        }
      });
      
      // Validate time format
      if (event.HHMMSS && !/^\d{2}:\d{2}:\d{2}$/.test(event.HHMMSS)) {
        errors.push({
          line: index + 2,
          field: 'HHMMSS',
          message: 'HHMMSS must be in format HH:MM:SS',
          severity: 'error'
        });
      }
    });
  }

  /**
   * Validate clusters file
   * @param {Object} file - File item
   * @param {Array} content - File content
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateClustersFile(file, content, errors, warnings) {
    if (!Array.isArray(content) || content.length === 0) {
      warnings.push({
        message: 'Clusters file is empty',
        severity: 'warning'
      });
      return;
    }
    
    const requiredFields = ['elements', 'xLocMM', 'yLocMM', 'meanPeakAmp', 'sumEnergy', 'sumCounts'];
    
    content.forEach((cluster, index) => {
      requiredFields.forEach(field => {
        if (cluster[field] === undefined || cluster[field] === null) {
          errors.push({
            line: index + 2,
            field,
            message: `Missing required field: ${field}`,
            severity: 'error'
          });
        }
      });
      
      // Validate numeric fields
      const numericFields = ['elements', 'xLocMM', 'yLocMM', 'meanPeakAmp', 'sumEnergy', 'sumCounts'];
      numericFields.forEach(field => {
        if (cluster[field] !== undefined && isNaN(cluster[field])) {
          errors.push({
            line: index + 2,
            field,
            message: `${field} must be a number`,
            severity: 'error'
          });
        }
      });
    });
  }

  /**
   * Validate reference points file
   * @param {Object} file - File item
   * @param {Array} content - File content
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateReferencePointsFile(file, content, errors, warnings) {
    if (!Array.isArray(content) || content.length === 0) {
      errors.push({
        message: 'Reference points file must contain at least one reference point',
        severity: 'error'
      });
      return;
    }
    
    const requiredFields = ['id', 'name', 'aeX', 'aeY', 'type'];
    
    content.forEach((point, index) => {
      requiredFields.forEach(field => {
        if (point[field] === undefined || point[field] === null) {
          errors.push({
            line: index + 2,
            field,
            message: `Missing required field: ${field}`,
            severity: 'error'
          });
        }
      });
      
      // Validate numeric fields
      const numericFields = ['aeX', 'aeY'];
      numericFields.forEach(field => {
        if (point[field] !== undefined && isNaN(point[field])) {
          errors.push({
            line: index + 2,
            field,
            message: `${field} must be a number`,
            severity: 'error'
          });
        }
      });
      
      // Validate type
      if (point.type && !['manhole', 'sump', 'reference'].includes(point.type)) {
        warnings.push({
          line: index + 2,
          field: 'type',
          message: 'type should be one of: manhole, sump, reference',
          severity: 'warning'
        });
      }
    });
  }

  /**
   * Validate version file
   * @param {Object} file - File item
   * @param {string} content - File content
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateVersionFile(file, content, errors, warnings) {
    if (!content || content.trim() === '') {
      errors.push({
        message: 'Version file cannot be empty',
        severity: 'error'
      });
      return;
    }
    
    // Check if version follows YYYY.MM.DD format
    const versionPattern = /^\d{4}\.\d{2}\.\d{2}$/;
    const version = content.trim();
    
    if (!versionPattern.test(version)) {
      warnings.push({
        message: 'Version should follow YYYY.MM.DD format',
        severity: 'warning'
      });
    }
  }

  /**
   * Validate image file
   * @param {Object} file - File item
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateImageFile(file, errors, warnings) {
    // Check file size (warning if larger than 10MB)
    if (file.size && file.size > 10 * 1024 * 1024) {
      warnings.push({
        message: 'Image file is larger than 10MB, consider optimizing',
        severity: 'warning'
      });
    }
    
    // Check naming convention
    const validPrefixes = ['bottom_texture_', 'top_texture_', 'side_texture_'];
    const hasValidPrefix = validPrefixes.some(prefix => file.name.startsWith(prefix));
    
    if (!hasValidPrefix) {
      warnings.push({
        message: 'Texture files should follow naming convention: {surface}_texture_{number}_{description}.png',
        severity: 'warning'
      });
    }
  }

  /**
   * Validate model file
   * @param {Object} file - File item
   * @param {Array} errors - Errors array
   * @param {Array} warnings - Warnings array
   */
  static validateModelFile(file, errors, warnings) {
    // Check file size (warning if larger than 50MB)
    if (file.size && file.size > 50 * 1024 * 1024) {
      warnings.push({
        message: 'Model file is larger than 50MB, consider optimizing',
        severity: 'warning'
      });
    }
    
    // Check file extension
    if (file.extension !== '.glb') {
      errors.push({
        message: 'Model files should be in GLB format',
        severity: 'error'
      });
    }
  }
}

export default ValidationService;
